import { useState, useEffect } from 'react';
import LecturerSidebar from '../components/LecturerSidebar.js';
import LecturerTermsModal from '../components/LecturerTermsModal.js';
import '../styles/LecturerDashboard.css';
import { BarChart2, BookOpen, Calendar, Clipboard, Clock10 } from 'lucide-react';
import { roomDetailLines } from '../utils/roomDisplay.js';

export default function LectureDashboardPanel() {
  // State for student statistic
  const [studentStats, setStudentStats] = useState(null);
  // State for upcoming courses
  const [upcomingCourses, setUpcomingCourses] = useState([]);
  // Loading states
  const [loadingStudentStats, setLoadingStudentStats] = useState(true);
  const [loadingUpcomingCourses, setLoadingUpcomingCourses] = useState(true);
  // State for assignments and loading state
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  // State for grades and loading state
  const [grades, setGrades] = useState([]);
  const [loadingGrades, setLoadingGrades] = useState(true);

  useEffect(() => {
    const fetchStudentStats = async () => {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/lecturer/dashboard/studentStats`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setStudentStats(data);
        } else {
          console.error('Error fetching student stats:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching student stats:', error);
      } finally {
        setLoadingStudentStats(false);
      }
    };

    const fetchUpcomingCourses = async () => {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/lecturer/dashboard/upcomingCourses`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUpcomingCourses(data);
        } else {
          console.error('Error fetching upcoming courses:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching upcoming courses:', error);
      } finally {
        setLoadingUpcomingCourses(false);
      }
    };

    const fetchAssignments = async () => {
      try {
        const token = localStorage.getItem('token');

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/lecturer/dashboard/assignments`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setAssignments(data);
        } else {
          console.error('Error fetching assignments:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
      } finally {
        setLoadingAssignments(false);
      }
    };

    const fetchGrades = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/lecturer/dashboard/gradesAvg`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setGrades(data);
        } else {
          console.error('Error fetching grades:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching grades:', error);
      } finally {
        setLoadingGrades(false);
      }
    };

    fetchStudentStats();
    fetchUpcomingCourses();
    fetchAssignments();
    fetchGrades();
  }, []);

  // Proste podsumowania do górnych kafelków
  const totalStudents = studentStats?.student_count ?? 0;
  const submittedTasks = studentStats?.submitted_tasks_count ?? 0;
  const upcomingCount = upcomingCourses.length;
  const assignmentsCount = assignments.length;

  return (
    <div className="lecture-dashboard-panel">
      <LecturerTermsModal />
      <LecturerSidebar />

      <div className="main-content">
        <div className="lecturer-page-inner">
          {/* HEADER */}
          <header className="lecturer-header">
            <div>
              <h1 className="lecturer-header-title">Panel główny</h1>
              <p className="lecturer-header-subtitle">
                Podsumowanie Twoich grup, zajęć i ocen.
              </p>
            </div>
          </header>

          {/* GÓRNY RZĄD STATYSTYK */}
          <section className="lecturer-stats-row">
            <div className="lecturer-stat-card blue">
              <p className="lecturer-stat-label">Kursanci łącznie</p>
              <p className="lecturer-stat-value">
                {loadingStudentStats ? '…' : totalStudents}
              </p>
              <p className="lecturer-stat-hint">
                Liczba unikalnych kursantów przypisanych do Twoich grup.
              </p>
            </div>

            <div className="lecturer-stat-card green">
              <p className="lecturer-stat-label">Odesłane zadania</p>
              <p className="lecturer-stat-value">
                {loadingStudentStats ? '…' : submittedTasks}
              </p>
              <p className="lecturer-stat-hint">
                Wszystkie prace oddane przez kursantów.
              </p>
            </div>

            <div className="lecturer-stat-card">
              <p className="lecturer-stat-label">Nadchodzące zajęcia</p>
              <p className="lecturer-stat-value">
                {loadingUpcomingCourses ? '…' : upcomingCount}
              </p>
              <p className="lecturer-stat-hint">
                Zajęcia z kalendarza w najbliższym czasie.
              </p>
            </div>

            <div className="lecturer-stat-card">
              <p className="lecturer-stat-label">Aktywne zadania</p>
              <p className="lecturer-stat-value">
                {loadingAssignments ? '…' : assignmentsCount}
              </p>
              <p className="lecturer-stat-hint">
                Zadania przypisane do prowadzonych przedmiotów.
              </p>
            </div>
          </section>

          {/* GŁÓWNY GRID */}
          <div className="lecturer-main-grid">
            {/* NADCHODZĄCE ZAJĘCIA */}
            <section className="lecturer-card-dashboard lecturer-card">
              <h2 className="lecturer-card-header">
                <BookOpen size={20} />
                <span className="lecturer-dashboard-card-title">
                  Nadchodzące zajęcia
                </span>
              </h2>

              {loadingUpcomingCourses ? (
                <p>Ładowanie zajęć...</p>
              ) : upcomingCourses.length === 0 ? (
                <p>Brak nadchodzących zajęć.</p>
              ) : (
                <ul className="lecturer-upcoming-courses-list">
                  {upcomingCourses.map((course) => (
                    <li
                      key={course.session_id}
                      className="lecturer-upcoming-course"
                    >
                      <div className="lecturer-upcoming-main">
                        <strong className="lecturer-upcoming-title">
                          {course.module_name}
                        </strong>
                        {course.title && (
                          <span className="lecturer-upcoming-subtitle">
                            {course.title}
                          </span>
                        )}
                      </div>
                      <div className="lecturer-upcoming-meta">
                        <span className="lecturer-meta-chip">
                          <Calendar size={14} />
                          <span>{course.session_date}</span>
                        </span>
                        <span className="lecturer-meta-chip">
                          <Clock10 size={14} />
                          <span>{course.godziny}</span>
                        </span>
                        <div className="lecturer-room-block">
                          {roomDetailLines(
                            course.room_name,
                            course.location,
                            course.room_comment
                          ).map((line, i) => (
                            <span key={i} className="lecturer-meta-inline">
                              {line.label}: <strong>{line.value}</strong>
                            </span>
                          ))}
                        </div>
                        <span className="lecturer-meta-inline">
                          Grupa: <strong>{course.group_name}</strong>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ZADANIA */}
            <section className="lecturer-card-dashboard lecturer-card">
              <h2 className="lecturer-card-header">
                <Clipboard size={20} />
                <span className="lecturer-dashboard-card-title">Zadania</span>
              </h2>

              {loadingAssignments ? (
                <p>Ładowanie zadań...</p>
              ) : assignments.length === 0 ? (
                <p>Brak przypisanych zadań.</p>
              ) : (
                <div className="lecturer-assignments-list">
                  {assignments.map((assignment, index) => (
                    <article key={index} className="lecturer-assignment">
                      <h3 className="lecturer-assignment-title">
                        {assignment.module_name} – {assignment.assignment_title}
                      </h3>
                      <p className="lecturer-assignment-desc">
                        {assignment.assignment_description}
                      </p>
                      <p className="lecturer-assignment-meta">
                        <span>
                          Termin: <strong>{assignment.due_date}</strong>
                        </span>
                        <span>
                          Grupy: <strong>{assignment.groups}</strong>
                        </span>
                      </p>
                    </article>
                  ))}
                </div>
              )}
            </section>

            {/* OCENY – NA CAŁĄ SZEROKOŚĆ */}
            <section className="lecturer-card-dashboard lecturer-card lecturer-card-full">
              <h2 className="lecturer-card-header">
                <BarChart2 size={20} />
                <span className="lecturer-dashboard-card-title">Oceny</span>
              </h2>

              {loadingGrades ? (
                <p>Ładowanie ocen...</p>
              ) : grades.length === 0 ? (
                <p>Brak ocen do wyświetlenia.</p>
              ) : (
                <div className="lecturer-grades-wrapper">
                  {grades.map((grade) => (
                    <div
                      key={grade.grupa}
                      className="lecturer-grade-group-block"
                    >
                      <h3 className="lecturer-grade-groups-title">
                        {grade.grupa}
                      </h3>
                      <table className="lecturer-grade-avg-table">
                        <thead>
                          <tr>
                            <th>Imię i nazwisko</th>
                            <th>Ocena</th>
                          </tr>
                        </thead>
                        <tbody>
                          {grade.studenci.map((student) => (
                            <tr key={student.student}>
                              <td>{student.student}</td>
                              <td>{student.ocena}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
